export type ProductId = 'gateway' | 'comply' | 'mds' | 'risk' | 'general';

export type NavTarget =
  | { kind: 'internal'; to: string; state?: { [k: string]: any } }
  | { kind: 'host'; href: string; state?: { [k: string]: any } };

export interface NavIconRef {
  pack: 'lucide' | 'product';
  name: string;
}

export type RolePreset =
  | 'tripleCheck'
  | 'socialWorker'
  | 'mdsRegional'
  | 'mdsNurse'
  | 'minimalCi'
  | 'ci'
  | 'general'
  | 'clinical'
  | 'clinicalManager'
  | 'clinicalCi'
  | 'report'
  | 'admin'
  | 'globalPortal';

/**
 * Visibility is gated by `title` membership in PRESET_TITLES (presets.ts) — the single
 * source ported from the legacy service. Nodes carry no preset list of their own.
 */
export interface NavNode {
  id: string;
  title: string;
  icon?: NavIconRef;
  capability?: string;
  productFlag?: ProductId;
  /** Panel section this screen sits under. Ungrouped screens render above the first section. */
  group?: string;
  // Display text when it differs from the legacy menu title. `title` stays the gating/parity key.
  label?: string;
  /** Short status pill rendered after the label, e.g. 'BETA'. Presentational only. */
  badge?: string;
  // Placeholder for a page not in the legacy menu yet: rendered muted, no link, skips role gating.
  disabled?: boolean;
  // Repeat of a screen that lives in another product: never the reason a product tile or group shows.
  mirror?: boolean;
  // Renders as a link on the rail above the product tiles, not inside its product's panel.
  rail?: boolean;
  /** Children gate on their own title, except INHERITED_CHILD_TITLES which ride on the parent. */
  children?: NavNode[];
}

/**
 * `rail` categories get a product tile; `settings` categories are reached from the avatar menu
 * (Admin is the one exception: it renders as its own tile below the products).
 */
export type NavPlacement = 'rail' | 'settings';

export interface NavCategory {
  product: ProductId;
  /** User-facing, task language ("Referrals"). What people navigate by. */
  title: string;
  /** Product/brand name shown as the panel subtitle ("Gateway"). */
  productLabel?: string;
  placement?: NavPlacement;
  icon: NavIconRef;
  productFlag?: ProductId;
  screens: NavNode[];
}

export interface ResolvedNavNode extends NavNode {
  label: string;
  target: NavTarget;
  children?: ResolvedNavNode[];
}

export interface ResolvedNavCategory {
  product: ProductId;
  title: string;
  productLabel?: string;
  placement: NavPlacement;
  icon: NavIconRef;
  screens: ResolvedNavNode[];
}

/** Panel section. `title` absent = the ungrouped block that renders first. */
export interface NavScreenGroup {
  title?: string;
  screens: ResolvedNavNode[];
}

/** One searchable destination for the command palette. */
export interface NavSearchEntry {
  title: string;
  path: string;
  product: ProductId;
  /** Where it lives, for the palette's secondary line ("Referrals › Census Trends"). */
  context: string;
  state?: { [k: string]: any };
}

export interface NavCtx {
  isCcare: boolean;
  referralResponseReportEnabled: boolean;
  highRiskEnabled?: boolean;
  /** Opt-in like highRiskEnabled: absent or false removes Discharge Plan. */
  dischargePlanEnabled?: boolean;
  /** Opt-in: absent or false keeps the MDS roles (134, 135) off the Users page. Set there from client settings, not by navCtx(). */
  mdsRolesEnabled?: boolean;
  gatewayReferralLinksDisabled?: boolean;
  /** Who sees the KPI pages (the app's canViewKpis); absent hides them from every role. */
  canViewKpis?: (role: number) => boolean;
}

export interface ResolveNavInput {
  roleNumber: number;
  can: (capability: string) => boolean;
  /** Absent / absent key ⇒ product enabled (backend field doesn't exist yet). */
  clientProducts?: Partial<Record<ProductId, boolean>>;
  ctx: NavCtx;
}

export interface BreadcrumbCrumb {
  title: string;
  path?: string;
}

/** Flat screen entry, shape-compatible with the legacy `AppScreen`. */
export interface NavScreen {
  title: string;
  path: string;
  state?: { [k: string]: any };
}
